import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createSupplier, listSuppliers } from "../../api/suppliersApi";
import type { SupplierOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWriteSuppliers } from "../../roles/permissions";

const COLUMNS: DataTableColumn<SupplierOut, keyof SupplierOut>[] = [
  { key: "name", header: "Name", render: (r) => r.name },
  { key: "phone", header: "Phone", render: (r) => r.phone ?? "—" },
  { key: "email", header: "Email", render: (r) => r.email ?? "—" },
];

/** Supplier directory — `GET/POST /suppliers` (§10.2). Feeds the product form's supplier
 * dropdown and purchase-order creation. */
export function SuppliersPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteSuppliers(user.role) : false;
  const suppliersState = useAsyncData(() => ensureFreshToken().then((token) => listSuppliers(token)), []);
  const { pageData: suppliersPageData, pagination: suppliersPagination, setPage: setSuppliersPage } = usePagination(
    suppliersState.data ?? [],
    25,
  );

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createSupplier(token, { name, phone: phone || undefined, email: email || undefined });
      setName("");
      setPhone("");
      setEmail("");
      suppliersState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create supplier.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Suppliers</h1>
          <p className="page__subtitle">Supplier directory — feeds the product form's supplier dropdown and purchase orders.</p>
        </div>
      </div>

      {suppliersState.error ? <StatusBanner kind="error">{suppliersState.error}</StatusBanner> : null}

      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) has read access to suppliers. Creating one is restricted to
          OWNER/BRANCH_MANAGER/ECOM_MGR by the backend (<code>require_roles</code> on <code>POST /suppliers</code>).
        </StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New supplier</h2>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Create supplier"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {suppliersState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={suppliersPageData}
            getRowId={(row) => row.id}
            pagination={suppliersPagination}
            onPageChange={setSuppliersPage}
            emptyMessage="No suppliers yet."
            caption="Suppliers"
          />
        )}
      </div>
    </div>
  );
}
